import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, Save } from 'lucide-react';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { IconButton } from '@/components/atoms/IconButton';
import { Input } from '@/components/atoms/Input';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/molecules/Card';
import { DataTable } from '@/components/organisms/DataTable';
import type { DataTableColumn } from '@/components/organisms/DataTable';
import { InviteDialog } from '@/components/organisms/InviteDialog';
import { ImportStudentsDialog } from '@/components/organisms/ImportStudentsDialog';
import { PendingInvitations } from '@/components/organisms/PendingInvitations';
import { buildRoute, ROUTES } from '@/constants/routes';
import { useCompany, useSaveCompany } from '@/hooks/useBackofficeData';
import { useEmployees } from '@/hooks/useCompanyData';
import { setCompanyScope } from '@/services/companyScope';
import type { Employee } from '@/types/company.types';
import styles from './CompanyDetailPage.module.scss';

/** Un `company_admin` no es alumno: no tiene nivel ni datos de aprendizaje. */
function isLearner(employee: Employee): boolean {
  return employee.role === 'student';
}

function roleLabel(employee: Employee): string {
  if (employee.role === 'company_admin') return 'Admin';
  if (employee.isCompanyAdmin) return 'Alumno + admin';
  return 'Alumno';
}

const userColumns: DataTableColumn<Employee>[] = [
  {
    key: 'user',
    header: 'Usuario',
    accessor: (row) => (
      <div className={styles.companyDetail__userCell}>
        <span className={styles.companyDetail__userName}>{row.name}</span>
        <span className={styles.companyDetail__userEmail}>{row.email}</span>
      </div>
    ),
    width: '40%',
  },
  {
    key: 'role',
    header: 'Rol',
    accessor: (row) => (
      <Badge variant={isLearner(row) ? 'default' : 'info'}>{roleLabel(row)}</Badge>
    ),
  },
  {
    key: 'level',
    header: 'Nivel',
    accessor: (row) => (isLearner(row) ? row.level : '—'),
    align: 'center',
  },
  {
    key: 'status',
    header: 'Estado',
    accessor: (row) => (
      <Badge variant={row.isActive ? 'success' : 'default'}>
        {row.isActive ? 'Activo' : 'Inactivo'}
      </Badge>
    ),
  },
];

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const [inviteOpen, setInviteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [invitationsRefreshKey, setInvitationsRefreshKey] = useState(0);
  const [employeesRefreshKey, setEmployeesRefreshKey] = useState(0);
  const [name, setName] = useState('');

  const { company, isLoading } = useCompany(isNew ? undefined : id);
  const { saveCompany, isSaving } = useSaveCompany();
  // Usuarios de la empresa (alumnos + admins). Se piden con el companyId
  // explícito: backoffice los consulta sin "entrar" en la empresa.
  const { employees, isLoading: isLoadingEmployees } = useEmployees(
    undefined,
    isNew ? undefined : id,
    employeesRefreshKey
  );

  // Al cargar la empresa (modo edición), precargamos el nombre.
  useEffect(() => {
    if (company?.name) setName(company.name);
  }, [company?.name]);

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    // El backend solo persiste `name` (el código se autogenera).
    const saved = await saveCompany(isNew ? null : (id ?? null), { name: trimmed });
    if (saved) {
      navigate(`${ROUTES.BACKOFFICE.COMPANIES}/${saved.id}`, { replace: true });
    }
  };

  // Backoffice administra cualquier empresa: abrirla lleva al área de empresa
  // (/company/*) con esa empresa fijada en el scope.
  const handleManage = () => {
    if (!id || !company) return;
    setCompanyScope({ id: company.id, name: company.name });
    navigate(ROUTES.COMPANY.DASHBOARD);
  };

  if (isLoading && !isNew) {
    return (
      <div className={styles.companyDetail}>
        <h1>Cargando...</h1>
      </div>
    );
  }

  return (
    <div className={styles.companyDetail}>
      <div className={styles.companyDetail__header}>
        <IconButton
          icon={ArrowLeft}
          onClick={() => navigate(ROUTES.BACKOFFICE.COMPANIES)}
          aria-label="Volver a Empresas"
        />
        <div className={styles.companyDetail__headerActions}>
          {!isNew && company && (
            <Button variant="outline" leftIcon={<Building2 />} onClick={handleManage}>
              Gestionar empresa
            </Button>
          )}
          <Button
            leftIcon={<Save />}
            onClick={handleSave}
            isLoading={isSaving}
            disabled={!name.trim()}
          >
            Guardar empresa
          </Button>
        </div>
      </div>

      <h1>{isNew ? 'Crear empresa' : company?.name || 'Detalles de empresa'}</h1>

      <Card>
        <CardHeader>
          <CardTitle>Información de la empresa</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={styles.companyDetail__field}>
            <label className={styles.companyDetail__label} htmlFor="company-name">
              Nombre
            </label>
            <Input
              id="company-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre de la empresa"
            />
          </div>
        </CardContent>
      </Card>

      {!isNew && id && (
        <>
          <div className={styles.companyDetail__sectionHeader}>
            <h2 className={styles.companyDetail__sectionTitle}>
              Usuarios{employees.length > 0 && ` (${employees.length})`}
            </h2>
            <div className={styles.companyDetail__sectionActions}>
              <Button variant="secondary" onClick={() => setImportOpen(true)}>
                Importar CSV
              </Button>
              <Button variant="primary" onClick={() => setInviteOpen(true)}>
                Invitar
              </Button>
            </div>
          </div>

          <DataTable
            columns={userColumns}
            data={employees}
            onRowClick={(employee) =>
              navigate(buildRoute(ROUTES.BACKOFFICE.USER_DETAIL, { id: employee.id }))
            }
            isLoading={isLoadingEmployees}
            emptyMessage="Esta empresa todavía no tiene usuarios. Invita al primero."
          />

          <PendingInvitations companyId={id} refreshKey={invitationsRefreshKey} />

          <InviteDialog
            open={inviteOpen}
            onOpenChange={setInviteOpen}
            companyId={id}
            allowRoleSelection
            onInvited={() => setInvitationsRefreshKey((k) => k + 1)}
          />

          <ImportStudentsDialog
            open={importOpen}
            onOpenChange={setImportOpen}
            companyId={id}
            companyName={company?.name ?? name}
            existingEmails={employees.map((e) => e.email)}
            onImported={() => setEmployeesRefreshKey((k) => k + 1)}
          />
        </>
      )}
    </div>
  );
}

export default CompanyDetailPage;
